import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  BuildingQuery,
  BuildingRow,
  BuildingSaveRequest,
  DeptQuery,
  DeptRow,
  DeptSaveRequest,
  FloorQuery,
  FloorRow,
  FloorSaveRequest,
  LocationQuery,
  LocationRow,
  LocationSaveRequest,
  OrgStructureDeleteResult,
  OrgStructureSaveResult,
  WarehouseQuery,
  WarehouseRow,
  WarehouseSaveRequest
} from '../types/orgStructure';

/**
 * 机构下挂的仓库与科室 / 地标：
 * 新机构必须先建仓库才能采购入库；科室二级库还要挂科室（科室数据范围据此折算）。
 */
export function createOrgStructureApi(http: HttpClient) {
  return {
    warehouse: {
      list(query: WarehouseQuery = {}): Promise<WarehouseRow[]> {
        return http.request<WarehouseRow[]>({ path: '/sys/warehouse', query: toQuery(query) });
      },
      save(body: WarehouseSaveRequest): Promise<OrgStructureSaveResult> {
        return http.request<OrgStructureSaveResult>({ path: '/sys/warehouse', method: 'POST', body });
      },
      remove(id: Id): Promise<OrgStructureDeleteResult> {
        return http.request<OrgStructureDeleteResult>({ path: `/sys/warehouse/${id}`, method: 'DELETE' });
      }
    },

    dept: {
      list(query: DeptQuery = {}): Promise<DeptRow[]> {
        return http.request<DeptRow[]>({ path: '/sys/dept', query: toQuery(query) });
      },
      save(body: DeptSaveRequest): Promise<OrgStructureSaveResult> {
        return http.request<OrgStructureSaveResult>({ path: '/sys/dept', method: 'POST', body });
      },
      remove(id: Id): Promise<OrgStructureDeleteResult> {
        return http.request<OrgStructureDeleteResult>({ path: `/sys/dept/${id}`, method: 'DELETE' });
      }
    },

    location: {
      list(query: LocationQuery = {}): Promise<LocationRow[]> {
        return http.request<LocationRow[]>({ path: '/sys/location', query: toQuery(query) });
      },
      save(body: LocationSaveRequest): Promise<OrgStructureSaveResult> {
        return http.request<OrgStructureSaveResult>({ path: '/sys/location', method: 'POST', body });
      },
      /** 货位上有库存时后端会拒绝删除。 */
      remove(id: Id): Promise<OrgStructureDeleteResult> {
        return http.request<OrgStructureDeleteResult>({ path: `/sys/location/${id}`, method: 'DELETE' });
      }
    },

    building: {
      list(query: BuildingQuery = {}): Promise<BuildingRow[]> {
        return http.request<BuildingRow[]>({ path: '/sys/building', query: toQuery(query) });
      },
      save(body: BuildingSaveRequest): Promise<OrgStructureSaveResult> {
        return http.request<OrgStructureSaveResult>({ path: '/sys/building', method: 'POST', body });
      },
      /** 楼宇下有楼层或科室定位时后端会拒绝删除。 */
      remove(id: Id): Promise<OrgStructureDeleteResult> {
        return http.request<OrgStructureDeleteResult>({ path: `/sys/building/${id}`, method: 'DELETE' });
      }
    },

    floor: {
      list(query: FloorQuery = {}): Promise<FloorRow[]> {
        return http.request<FloorRow[]>({ path: '/sys/floor', query: toQuery(query) });
      },
      save(body: FloorSaveRequest): Promise<OrgStructureSaveResult> {
        return http.request<OrgStructureSaveResult>({ path: '/sys/floor', method: 'POST', body });
      },
      /** 楼层上仍有科室/地标时后端会拒绝删除。 */
      remove(id: Id): Promise<OrgStructureDeleteResult> {
        return http.request<OrgStructureDeleteResult>({ path: `/sys/floor/${id}`, method: 'DELETE' });
      }
    }
  };
}

export type OrgStructureApi = ReturnType<typeof createOrgStructureApi>;
